// TelcoTrack — období seznamu stavebního deníku (kalendářní dny v Europe/Prague).

import { normalizeDiaryEntryDateIso } from '@core/shared-bridge';
import {
  isCompleteDateRangeFilter,
  toCsDateOnly,
  toIsoDateOnly,
} from '../../shared/utils/date-filter.util';

/** Dnešní datum jako YYYY-MM-DD v Europe/Prague. */
export function pragueTodayIso(): string {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Prague',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** Datum deníku pro UI — date-only v Europe/Prague, bez UTC posunu YYYY-MM-DD. */
export function formatDiaryEntryDateCs(
  value?: string | Date | null,
  style: 'short' | 'long' = 'short',
): string {
  const day = normalizeDiaryEntryDateIso(value);
  if (!day) return '—';
  const [y, m, d] = day.split('-').map(Number);
  const noonUtc = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  return new Intl.DateTimeFormat('cs-CZ', {
    timeZone: 'Europe/Prague',
    day: style === 'long' ? 'numeric' : '2-digit',
    month: style === 'long' ? 'long' : '2-digit',
    year: 'numeric',
  }).format(noonUtc);
}

/** Dnešní datum ve formátu filtru Od–Do (`dd.mm.yyyy`). */
export function defaultDiaryListCsDate(): string {
  return toCsDateOnly(pragueTodayIso());
}

export function isDiaryListDefaultTodayRange(
  from: string | null | undefined,
  to: string | null | undefined,
): boolean {
  if (!isCompleteDateRangeFilter(from, to)) return false;
  const today = pragueTodayIso();
  return toIsoDateOnly(from!) === today && toIsoDateOnly(to!) === today;
}

/**
 * URL seznamu: `df`/`dt` jako Objekty/Výkaz, případně `dateFrom`/`dateTo`.
 * Chybějící nebo neplatný rozsah → dnes–dnes.
 */
export function parseDiaryListDateRangeFromQuery(query: {
  df?: string | null;
  dt?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}): { dateFrom: string; dateTo: string } {
  const todayCs = defaultDiaryListCsDate();
  const rawFrom = (query.df ?? query.dateFrom ?? '').trim();
  const rawTo = (query.dt ?? query.dateTo ?? '').trim();
  if (!rawFrom || !rawTo) {
    return { dateFrom: todayCs, dateTo: todayCs };
  }
  const dateFrom = toCsDateOnly(rawFrom);
  const dateTo = toCsDateOnly(rawTo);
  if (!isCompleteDateRangeFilter(dateFrom, dateTo)) {
    return { dateFrom: todayCs, dateTo: todayCs };
  }
  return { dateFrom, dateTo };
}

/**
 * Do URL zapisuje jen vlastní Od–Do (`df`/`dt` ISO).
 * Výchozí dnes–dnes se vynechá (stejně jako dříve `period=today`).
 */
export function diaryListDateRangeQueryParams(
  from: string | null | undefined,
  to: string | null | undefined,
): { df?: string; dt?: string } {
  if (!isCompleteDateRangeFilter(from, to) || isDiaryListDefaultTodayRange(from, to)) {
    return {};
  }
  return {
    df: toIsoDateOnly(from!),
    dt: toIsoDateOnly(to!),
  };
}

export function diaryListDateRangeLabel(
  from: string | null | undefined,
  to: string | null | undefined,
): string {
  if (!isCompleteDateRangeFilter(from, to)) {
    return defaultDiaryListCsDate();
  }
  const a = toCsDateOnly(toIsoDateOnly(from!));
  const b = toCsDateOnly(toIsoDateOnly(to!));
  return a === b ? a : `${a} – ${b}`;
}
